import React from 'react'
import { Spin } from 'antd'
import { LIBRARY_CATEGORIES, isProcessing } from '@loc/server/utils/music'
import { logger } from '@loc/helpers/src/logger/client'
import { useUnmount } from 'ahooks'
import { match } from 'ts-pattern'
import { Trans, useLingui } from '@lingui/react/macro'

import { EmptyData } from '../../ai-music-generator/_blocks/EmptyData'
import TadTabs from '../../ai-music-generator/_components/Tabs'
import MusicItem from '../MusicItem'
import TadPagination from '../Pagination'

import { transformMusicData } from './_helpers/data'

import type { MusicListDataItem } from '../../ai-music-generator/_types/trpc'
import type {
  MusicMetadataSuccess,
  showType,
} from '../MusicItem/_components/InternalMusicItem/_components/MusicItemSuccess'
import type { LibraryCategory } from '@loc/server/utils/music'

import { useSiteSpecificContext } from '@/contexts/SiteSpecificContext'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { FluentFlex } from '@/components/FluentFlex'
import { useScreenMatched } from '@/hooks/screen'


import { useState, useRef, useMemo, useImperativeHandle, useEffect } from 'react';
import { api } from '@/utils/api';
import { cls } from 'tagged-classnames-free';
import { CssIcon } from '@/components/CssIcon';
const DEFAULT_PAGE_SIZE = 9
const LARGE_SCREEN_PAGE_SIZE = 18
const PROCESSING_TIMEOUT_MS = 2e3
type CategorySwitchLoadingState = 'idle' | 'waiting' | 'fetching'

export interface MusicListActionRef {
  /**
   * force 表示是否强行刷新，默认 false
   *
   * 强行刷新不会渲染数据为空的组件
   */
  refetchIfPage1: (force?: boolean) => void
}

export interface MusicListProps {
  className?: string
  listType?: string
  showTypes?: showType[]
  onlyExpand?: boolean
}

const MusicList = (
  props: MusicListProps,
  ref: React.Ref<MusicListActionRef>,
) => {
  const { className, listType, showTypes: propsShowTypes, onlyExpand } = props
  const { t } = useLingui()
  const [forceCount, setForceCount] = useState(0)
  const [current, setCurrent] = useState(1)
  const [libraryCategory, setLibraryCategory] = useState<LibraryCategory>(
    LIBRARY_CATEGORIES.All,
  )
  const [categorySwitchLoadingState, setCategorySwitchLoadingState] =
    useState<CategorySwitchLoadingState>('idle')
  const { matched: is3xl, ready: screenReady } = useScreenMatched('3xl')
  const pageSize = is3xl ? LARGE_SCREEN_PAGE_SIZE : DEFAULT_PAGE_SIZE
  const prevPageSizeRef = useRef(pageSize)
  const currentForPageSize = useMemo(() => {
    const prevPageSize = prevPageSizeRef.current
    if (prevPageSize === pageSize) {
      return current
    }

    const firstItemIndex = (current - 1) * prevPageSize
    return Math.floor(firstItemIndex / pageSize) + 1
  }, [current, pageSize])
  const { isSignIn, sessionLoading } = useSessionInfo()
  const isLikeList = listType === 'liked'
  const showTypes =
    propsShowTypes ||
    match(listType)
      .with('liked', () => [
        { key: 'liked', iconOnly: false },
        'labels',
        'duration',
        'share',
        'download',
      ])
      .with('all', () => ['labels', 'duration', 'share', 'download'])
      .otherwise(() => ['labels', 'duration', 'share', 'download'])

  const { creator } = useSiteSpecificContext()
  const selectedLibraryCategory =
    listType !== 'liked' ? libraryCategory : undefined
  const {
    data: { musics: myLibraryData, total: myLibraryTotal } = {
      musics: [],
      total: 0,
    },
    isFetching: myLibraryIsFetching,
    refetch: myLibraryRefetch,
    remove: myLibraryRemove,
  } = api.music_v2.getMyLibraryList.useQuery(
    {
      pageNumber: currentForPageSize,
      pageSize,
      category: selectedLibraryCategory,
    },
    {
      enabled: isSignIn && !isLikeList && screenReady,
      keepPreviousData: true,
    },
  )
  const {
    data: { musics: likedData, total: likedTotal } = { musics: [], total: 0 },
    isFetching: likedIsFetching,
    refetch: likedRefetch,
    remove: likedRemove,
  } = api.music_v2.getLikeList.useQuery(
    {
      pageNumber: currentForPageSize,
      pageSize,
    },
    {
      enabled: isSignIn && isLikeList && screenReady,
      keepPreviousData: true,
    },
  )
  const remove = isLikeList ? likedRemove : myLibraryRemove
  const refetch = isLikeList ? likedRefetch : myLibraryRefetch
  const data = isLikeList ? likedData : myLibraryData
  const isFetching = isLikeList ? likedIsFetching : myLibraryIsFetching
  const total = isLikeList ? likedTotal : myLibraryTotal
  useUnmount(() => {
    remove()
  })

  const { mutateAsync: retryAsync } = api.music.retry.useMutation()
  const { mutateAsync: pollingAsync } = api.music.polling.useMutation()

  useImperativeHandle(ref, () => {
    return {
      refetchIfPage1: (force = false) => {
        if (force) {
          setForceCount((prev) => prev + 1)
        }

        if (current === 1) {
          refetch()
        }
      },
    }
  })

  const { mergedData, successData } = useMemo(() => {
    if (!data) {
      return { mergedData: [], successData: [] }
    }
    const mergedData = data.map((item: MusicListDataItem) => {
      return transformMusicData(item, isLikeList)
    })
    const successData = mergedData.filter(
      (item) => item.status === 'success',
    ) as MusicMetadataSuccess[]
    return { mergedData, successData }
  }, [data, isLikeList])

  const hasData = mergedData.length > 0
  // 非 liked 列表依赖 creator 信息；其未就绪时保持 loading，避免提前显示 empty。
  const isCreatorPending =
    !!isSignIn &&
    !isLikeList &&
    !creator.data &&
    (creator.isLoading || creator.isInitialLoading)

  const isForcePending = forceCount > 0

  // 列表区域三态：loading -> list -> empty（empty 只在请求结束且无数据时展示）
  const shouldShowLoading =
    !screenReady ||
    sessionLoading ||
    isCreatorPending ||
    (isForcePending && !hasData) ||
    (isFetching && !hasData)
  const shouldOverlayCategorySwitchLoading =
    categorySwitchLoadingState !== 'idle'
  const shouldShowList = hasData
  const doRenderPagination = typeof total === 'number' && total > pageSize

  const categoryTabs =
    listType !== 'liked'
      ? [
          {
            label: t`ALL`,
            key: LIBRARY_CATEGORIES.All,
          },
          {
            label: t`AI Music`,
            key: LIBRARY_CATEGORIES.AiMusic,
          },
          {
            label: t`AI Cover`,
            key: LIBRARY_CATEGORIES.AiCover,
          },
          {
            label: t`Text to Speech`,
            key: LIBRARY_CATEGORIES.TextToSpeech,
          },
        ]
      : []

  useEffect(() => {
    if (!screenReady) {
      return
    }

    const prevPageSize = prevPageSizeRef.current
    if (prevPageSize === pageSize) {
      return
    }

    setCurrent(currentForPageSize)
    prevPageSizeRef.current = pageSize
  }, [currentForPageSize, pageSize, screenReady])

  useEffect(() => {
    if (!isForcePending) {
      return
    }

    // force 刷新用于兜住首屏空窗，请求结束后立即清理。
    if (!isFetching) {
      setForceCount(0)
    }
  }, [isFetching, isForcePending])

  useEffect(() => {
    if (isLikeList || categorySwitchLoadingState === 'idle') {
      return
    }

    if (categorySwitchLoadingState === 'waiting' && myLibraryIsFetching) {
      setCategorySwitchLoadingState('fetching')
      return
    }

    if (categorySwitchLoadingState === 'fetching' && !myLibraryIsFetching) {
      setCategorySwitchLoadingState('idle')
    }
  }, [categorySwitchLoadingState, isLikeList, myLibraryIsFetching])

  useEffect(() => {
    if (mergedData.length === 0) {
      logger.debug(`No merged data, skip`)
      return
    }

    let loading = false
    let count = 0

    const processingData = data?.filter((item) => isProcessing(item))

    if (!processingData || processingData.length === 0) {
      logger.debug(`No processing data, skip`)
      return
    }

    const task = async () => {
      if (loading) {
        logger.debug(`Task is running, skip`)
        return
      }

      try {
        loading = true
        count += 1
        const result = await pollingAsync(
          processingData.map((item) => item.uuid),
        )

        if (
          result.filter((item) => isProcessing(item)).length !==
          processingData.length
        ) {
          refetch()
        }
      } catch (err) {
        logger.error(err)
      } finally {
        loading = false
        logger.debug(`Fetch processing data ${count} times`)
      }
    }

    const timer = window.setInterval(() => {
      task()
    }, PROCESSING_TIMEOUT_MS)

    return () => {
      window.clearInterval(timer)
    }
  }, [mergedData, data, pollingAsync, refetch])

  return (
    <FluentFlex className={cls`flex-1 flex-col px-4 py-5 md:px-6 ${className}`}>
      {listType === undefined ? (
        <div
          className={cls`mb-5 flex items-center gap-2 space-x-2 text-[22px]`}
        >
          <CssIcon className='i-cus--tad-library size-6' />
          <Trans>My Library</Trans>
        </div>
      ) : null}

      {listType !== 'liked' ? (
        <div className={cls`overflow-x-auto`}>
          <TadTabs
            className={cls`mb-5 [&_.coco-tabs-tab+.coco-tabs-tab]:!ml-5 [&_.coco-tabs-tab]:!pb-1 [&_.coco-tabs-tab]:!pt-0`}
            size='large'
            themeConfig={{
              horizontalItemGutter: 36,
            }}
            activeKey={selectedLibraryCategory}
            items={categoryTabs}
            onChange={(activeKey) => {
              if (activeKey === selectedLibraryCategory) {
                return
              }

              setCategorySwitchLoadingState('waiting')
              setLibraryCategory(activeKey as LibraryCategory)
              setCurrent(1)
            }}
          />
        </div>
      ) : null}

      <FluentFlex>
        {shouldShowLoading ? (
          <div
            className={cls`flex min-h-[240px] flex-1 items-center justify-center`}
          >
            <Spin spinning />
          </div>
        ) : shouldShowList ? (
          <>
            <Spin
              delay={200}
              spinning={shouldOverlayCategorySwitchLoading}
              rootClassName={cls`scrollbar flex flex-1 overflow-auto`}
              wrapperClassName={cls`
                scrollbar flex flex-1 overflow-auto
                [&_.coco-spin-container]:flex [&_.coco-spin-container]:flex-1
                [&_.coco-spin-container]:overflow-auto
              `}
            >
              <FluentFlex
                className={cls`scrollbar-hover flex-col`}
                key={listType}
              >
                <div
                  className={cls`3xl:grid-cols-2 grid grid-cols-1 gap-x-3 gap-y-1`}
                >
                  {mergedData.map((item) => {
                    return (
                      <MusicItem
                        key={item.id}
                        data={item}
                        onRetry={async () => {
                          if (item.status === 'error') {
                            await retryAsync(item.id)
                            refetch()
                          }
                        }}
                        fromList={successData}
                        onDeleted={() => {
                          refetch()
                        }}
                        showTypes={showTypes as showType[]}
                        onlyExpand={onlyExpand || isLikeList}
                        musicOptionsProps={{
                          btnClassName: cls`w-auto !flex-row`,
                          dropdownHidden: ['download', 'share'],
                        }}
                      />
                    )
                  })}
                </div>

                {doRenderPagination && (
                  <div className={`flex justify-end p-4`}>
                    <TadPagination
                      hideOnSinglePage
                      pageSize={pageSize}
                      showSizeChanger={false}
                      showQuickJumper
                      total={total}
                      current={currentForPageSize}
                      onChange={(current) => {
                        setCurrent(current)
                      }}
                    />
                  </div>
                )}
              </FluentFlex>
            </Spin>
            {/* <SimpleMusicDetail
              data={meta ?? null}
              className={`scrollbar hidden h-full overflow-auto 2xl:flex`}
            /> */}
          </>
        ) : (
          <EmptyData listType={listType} />
        )}
      </FluentFlex>
    </FluentFlex>
  )
}

const ForwardRefMusicList = React.forwardRef(MusicList)

export default ForwardRefMusicList
